import { compileEbaOverlay, EBA_COMPILE_PROFILES, type PayRuleBlock } from './eba-block-compiler';
import type { AllowanceBasis, EmploymentTypeKey, PenaltyBand } from '../award-rate-lookup';

const COMPILED_SOURCE = 'COMPILED_FROM_BLOCKS';
const NUMERIC_TOLERANCE = 1e-9;

export interface PersistedPenaltyLineForDrift {
  source?: string | null;
  band: string;
  employmentType?: string | null;
  employment_type?: string | null;
  classificationStream?: string | null;
  classification_stream?: string | null;
  multiplier: number | string;
  includesCasualLoading?: boolean | null;
  includes_casual_loading?: boolean | null;
}

export interface PersistedAllowanceLineForDrift {
  source?: string | null;
  allowanceCode?: string | null;
  allowance_code?: string | null;
  basis: string;
  amount: number | string;
  appliesToEmployment?: string | null;
  applies_to_employment?: string | null;
}

export interface NormalizedPenaltyLine {
  kind: 'PENALTY';
  key: string;
  band: PenaltyBand;
  employmentType: 'PERMANENT' | 'CASUAL';
  classificationStream: string | null;
  multiplier: number;
  includesCasualLoading: boolean;
}

export interface NormalizedAllowanceLine {
  kind: 'ALLOWANCE';
  key: string;
  allowanceCode: string;
  basis: AllowanceBasis;
  amount: number;
  appliesToEmployment: EmploymentTypeKey | null;
}

export type NormalizedCompiledLine = NormalizedPenaltyLine | NormalizedAllowanceLine;

export interface EbaDriftDifference {
  field: string;
  expected: string | number | boolean | null;
  persisted: string | number | boolean | null;
}

export interface EbaMissingCompiledLine {
  kind: NormalizedCompiledLine['kind'];
  key: string;
  expected: NormalizedCompiledLine;
}

export interface EbaUnexpectedCompiledLine {
  kind: NormalizedCompiledLine['kind'];
  key: string;
  persisted: NormalizedCompiledLine;
}

export interface EbaMismatchedCompiledLine {
  kind: NormalizedCompiledLine['kind'];
  key: string;
  expected: NormalizedCompiledLine;
  persisted: NormalizedCompiledLine;
  differences: EbaDriftDifference[];
}

export interface EbaCompiledLineDriftResult {
  inSync: boolean;
  missing: EbaMissingCompiledLine[];
  unexpected: EbaUnexpectedCompiledLine[];
  mismatched: EbaMismatchedCompiledLine[];
}

export interface DetectEbaCompiledLineDriftInput {
  blocks: PayRuleBlock[];
  baseAwardCode: string;
  persistedPenaltyLines: PersistedPenaltyLineForDrift[];
  persistedAllowanceLines: PersistedAllowanceLineForDrift[];
}

function normalizedSource(source: string | null | undefined): string {
  return (source ?? '').trim().toUpperCase();
}

function normalizeNullableString(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function normalizeUpperNullable(value: string | null | undefined): string | null {
  return normalizeNullableString(value)?.toUpperCase() ?? null;
}

function normalizeNumber(value: number | string): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) {
    throw new Error(`eba_drift_detector_invalid_numeric_value:${String(value)}`);
  }
  return parsed;
}

function penaltyKey(
  band: string,
  employmentType: string,
  classificationStream: string | null,
): string {
  return `PENALTY|${band}|${employmentType}|${classificationStream ?? ''}`;
}

function allowanceKey(allowanceCode: string, appliesToEmployment: string | null): string {
  return `ALLOWANCE|${allowanceCode}|${appliesToEmployment ?? ''}`;
}

function normalizeCompiledPenaltyLine(line: {
  band: PenaltyBand;
  employmentType: 'PERMANENT' | 'CASUAL';
  classificationStream: string | null;
  multiplier: number;
  includesCasualLoading?: boolean;
}): NormalizedPenaltyLine {
  const classificationStream = normalizeNullableString(line.classificationStream);
  return {
    kind: 'PENALTY',
    key: penaltyKey(line.band, line.employmentType, classificationStream),
    band: line.band,
    employmentType: line.employmentType,
    classificationStream,
    multiplier: line.multiplier,
    includesCasualLoading: line.includesCasualLoading === true,
  };
}

function normalizePersistedPenaltyLine(line: PersistedPenaltyLineForDrift): NormalizedPenaltyLine {
  const band = normalizeUpperNullable(line.band) as PenaltyBand | null;
  const employmentType = normalizeUpperNullable(line.employmentType ?? line.employment_type) as
    | 'PERMANENT'
    | 'CASUAL'
    | null;
  if (!band || !employmentType) {
    throw new Error('eba_drift_detector_invalid_persisted_penalty_identity');
  }

  const classificationStream = normalizeNullableString(
    line.classificationStream ?? line.classification_stream,
  );
  const includesCasualLoading =
    line.includesCasualLoading === true || line.includes_casual_loading === true;
  return {
    kind: 'PENALTY',
    key: penaltyKey(band, employmentType, classificationStream),
    band,
    employmentType,
    classificationStream,
    multiplier: normalizeNumber(line.multiplier),
    includesCasualLoading,
  };
}

function driftProfileForBaseAward(baseAwardCode: string) {
  return EBA_COMPILE_PROFILES[baseAwardCode] ?? null;
}

/** P60 — compare persisted/compiled casual penalties on the base multiplier. */
function normalizeCasualPenaltyToBase(
  line: NormalizedPenaltyLine,
  profile: ReturnType<typeof driftProfileForBaseAward>,
): number {
  if (line.employmentType !== 'CASUAL' || !profile) return line.multiplier;
  if (
    profile.casualPenaltyConvention === 'BASE_PLUS_ENGINE_LOADING' &&
    line.includesCasualLoading
  ) {
    return line.multiplier / (1 + profile.casualLoadingRate);
  }
  return line.multiplier;
}

function normalizeCompiledAllowanceLine(line: {
  allowanceCode: string;
  basis: AllowanceBasis;
  amount: number;
  appliesToEmployment?: EmploymentTypeKey | null;
}): NormalizedAllowanceLine {
  const allowanceCode = line.allowanceCode.trim().toUpperCase();
  const appliesToEmployment = normalizeUpperNullable(
    line.appliesToEmployment,
  ) as EmploymentTypeKey | null;
  return {
    kind: 'ALLOWANCE',
    key: allowanceKey(allowanceCode, appliesToEmployment),
    allowanceCode,
    basis: line.basis,
    amount: line.amount,
    appliesToEmployment,
  };
}

function normalizePersistedAllowanceLine(
  line: PersistedAllowanceLineForDrift,
): NormalizedAllowanceLine {
  const allowanceCode = normalizeUpperNullable(line.allowanceCode ?? line.allowance_code);
  const basis = normalizeUpperNullable(line.basis) as AllowanceBasis | null;
  if (!allowanceCode || !basis) {
    throw new Error('eba_drift_detector_invalid_persisted_allowance_identity');
  }
  const appliesToEmployment = normalizeUpperNullable(
    line.appliesToEmployment ?? line.applies_to_employment,
  ) as EmploymentTypeKey | null;
  return {
    kind: 'ALLOWANCE',
    key: allowanceKey(allowanceCode, appliesToEmployment),
    allowanceCode,
    basis,
    amount: normalizeNumber(line.amount),
    appliesToEmployment,
  };
}

function mapByKey<T extends NormalizedCompiledLine>(lines: T[]): Map<string, T> {
  return new Map(lines.map((line) => [line.key, line]));
}

function numbersMatch(left: number, right: number): boolean {
  return Math.abs(left - right) <= NUMERIC_TOLERANCE;
}

function differencesForLine(
  expected: NormalizedCompiledLine,
  persisted: NormalizedCompiledLine,
  baseAwardCode: string,
): EbaDriftDifference[] {
  if (expected.kind !== persisted.kind) {
    return [{ field: 'kind', expected: expected.kind, persisted: persisted.kind }];
  }

  if (expected.kind === 'PENALTY' && persisted.kind === 'PENALTY') {
    const profile = driftProfileForBaseAward(baseAwardCode);
    const differences: EbaDriftDifference[] = [];
    if (expected.includesCasualLoading !== persisted.includesCasualLoading) {
      // Compiled base/false may match persisted loaded/true after rematerialisation.
      const rematerialisationFlagPair =
        (expected.includesCasualLoading && !persisted.includesCasualLoading) ||
        (!expected.includesCasualLoading && persisted.includesCasualLoading);
      if (!rematerialisationFlagPair) {
        differences.push({
          field: 'includesCasualLoading',
          expected: expected.includesCasualLoading,
          persisted: persisted.includesCasualLoading,
        });
      }
    }
    const expectedBase = normalizeCasualPenaltyToBase(expected, profile);
    const persistedBase = normalizeCasualPenaltyToBase(persisted, profile);
    if (!numbersMatch(expectedBase, persistedBase)) {
      differences.push({
        field: 'multiplier',
        expected: expectedBase,
        persisted: persistedBase,
      });
    }
    return differences;
  }

  if (expected.kind === 'ALLOWANCE' && persisted.kind === 'ALLOWANCE') {
    const differences: EbaDriftDifference[] = [];
    if (expected.basis !== persisted.basis) {
      differences.push({ field: 'basis', expected: expected.basis, persisted: persisted.basis });
    }
    if (!numbersMatch(expected.amount, persisted.amount)) {
      differences.push({ field: 'amount', expected: expected.amount, persisted: persisted.amount });
    }
    return differences;
  }

  return [];
}

export function detectEbaCompiledLineDrift(
  input: DetectEbaCompiledLineDriftInput,
): EbaCompiledLineDriftResult {
  const compiled = compileEbaOverlay(input.blocks, input.baseAwardCode);
  const expectedLines: NormalizedCompiledLine[] = [
    ...compiled.penalties.map(normalizeCompiledPenaltyLine),
    ...compiled.allowances.map(normalizeCompiledAllowanceLine),
  ];
  const persistedLines: NormalizedCompiledLine[] = [
    ...input.persistedPenaltyLines
      .filter((line) => normalizedSource(line.source) === COMPILED_SOURCE)
      .map(normalizePersistedPenaltyLine),
    ...input.persistedAllowanceLines
      .filter((line) => normalizedSource(line.source) === COMPILED_SOURCE)
      .map(normalizePersistedAllowanceLine),
  ];

  const expectedByKey = mapByKey(expectedLines);
  const persistedByKey = mapByKey(persistedLines);
  const missing: EbaMissingCompiledLine[] = [];
  const unexpected: EbaUnexpectedCompiledLine[] = [];
  const mismatched: EbaMismatchedCompiledLine[] = [];

  for (const [key, expected] of expectedByKey.entries()) {
    const persisted = persistedByKey.get(key);
    if (!persisted) {
      missing.push({ kind: expected.kind, key, expected });
      continue;
    }

    const differences = differencesForLine(expected, persisted, input.baseAwardCode);
    if (differences.length > 0) {
      mismatched.push({ kind: expected.kind, key, expected, persisted, differences });
    }
  }

  for (const [key, persisted] of persistedByKey.entries()) {
    if (!expectedByKey.has(key)) {
      unexpected.push({ kind: persisted.kind, key, persisted });
    }
  }

  return {
    inSync: missing.length === 0 && unexpected.length === 0 && mismatched.length === 0,
    missing: missing.sort((a, b) => a.key.localeCompare(b.key)),
    unexpected: unexpected.sort((a, b) => a.key.localeCompare(b.key)),
    mismatched: mismatched.sort((a, b) => a.key.localeCompare(b.key)),
  };
}

/**
 * G-8 — s 206 floors an EA *base rate* against the award. It does not lift
 * dollar-fixed allowances. Compare EA dollar amounts to the current award
 * line for the same code/employment. No match = no invented floor.
 */
export type DollarAllowanceForAwardFloor = {
  allowanceCode?: string | null;
  allowance_code?: string | null;
  amount: number | string;
  appliesToEmployment?: string | null;
  applies_to_employment?: string | null;
};

export type EbaAllowanceAwardFloorDrift = {
  allowanceCode: string;
  appliesToEmployment: string | null;
  eaAmount: number;
  awardAmount: number;
  shortfall: number;
};

export type EbaAllowanceAwardFloorResult = {
  underAward: EbaAllowanceAwardFloorDrift[];
};

function allowanceFloorKey(code: string, employment: string | null): string {
  return `${code}|${employment ?? ''}`;
}

function normalizeDollarAllowance(line: DollarAllowanceForAwardFloor): {
  key: string;
  allowanceCode: string;
  appliesToEmployment: string | null;
  amount: number;
} | null {
  const allowanceCode = normalizeUpperNullable(line.allowanceCode ?? line.allowance_code);
  if (!allowanceCode) return null;
  const appliesToEmployment = normalizeUpperNullable(
    line.appliesToEmployment ?? line.applies_to_employment,
  );
  return {
    key: allowanceFloorKey(allowanceCode, appliesToEmployment),
    allowanceCode,
    appliesToEmployment,
    amount: normalizeNumber(line.amount),
  };
}

function amountToCents(amount: number): number {
  // Scale a stored dollar amount to integer cents for comparison only.
  // eslint-disable-next-line statura/no-floating-point-money -- integer-cent compare of persisted award/EA lines; no pay calc
  return Math.round(amount * 100);
}

export function detectEbaDollarAllowanceAwardFloorDrift(input: {
  eaAllowances: readonly DollarAllowanceForAwardFloor[];
  awardAllowances: readonly DollarAllowanceForAwardFloor[];
}): EbaAllowanceAwardFloorResult {
  const awardByKey = new Map<string, { amount: number }>();
  for (const line of input.awardAllowances) {
    const normalized = normalizeDollarAllowance(line);
    if (!normalized) continue;
    awardByKey.set(normalized.key, { amount: normalized.amount });
  }

  const underAward: EbaAllowanceAwardFloorDrift[] = [];
  const seen = new Set<string>();
  for (const line of input.eaAllowances) {
    const normalized = normalizeDollarAllowance(line);
    if (!normalized || seen.has(normalized.key)) continue;
    const award = awardByKey.get(normalized.key);
    if (!award) continue;
    const eaCents = amountToCents(normalized.amount);
    const awardCents = amountToCents(award.amount);
    if (eaCents >= awardCents) continue;
    seen.add(normalized.key);
    underAward.push({
      allowanceCode: normalized.allowanceCode,
      appliesToEmployment: normalized.appliesToEmployment,
      eaAmount: normalized.amount,
      awardAmount: award.amount,
      shortfall: (awardCents - eaCents) / 100,
    });
  }

  return {
    underAward: underAward.sort((a, b) => a.allowanceCode.localeCompare(b.allowanceCode)),
  };
}

export function formatEbaAllowanceAwardFloorWarning(
  drifts: readonly EbaAllowanceAwardFloorDrift[],
): { code: 'EBA_ALLOWANCE_BELOW_AWARD'; message: string } | null {
  if (drifts.length === 0) return null;
  const names = drifts
    .map(
      (drift) =>
        `${drift.allowanceCode} ($${drift.eaAmount.toFixed(2)} < $${drift.awardAmount.toFixed(2)})`,
    )
    .join(', ');
  return {
    code: 'EBA_ALLOWANCE_BELOW_AWARD',
    message: `s 206 floors the EA base rate, not dollar allowances. ${drifts.length} EA allowance${drifts.length === 1 ? ' is' : 's are'} below the current award: ${names}.`,
  };
}
